import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import GetCookieByName from '../../hooks/GetCookie';
import { useDebounce } from '../../hooks/useDebounce';
import {
    Box,
    Typography,
    Button,
    Tabs,
    Tab,
    TextField,
    InputAdornment,
    Paper,
    Grid,
    Pagination,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import { useBriefs, useDeleteBrief, useMentionsCount, useUserLookup } from '../../hooks/useBriefs';
import { useSnackbar } from 'notistack';
import { STATUS_OPTIONS } from './briefOptions';
import { labelForStatus } from './briefUiUtils';
import BriefCard from './BriefCard';
import CreateBriefsModal from './CreateBriefsModal';
import BriefDetailDialog from './BriefDetailDialog';
import WorkTracker from './WorkTracker';
import DashboardHeaderSection from '../common/dashboardheader/DashboardHeader';

// "Delayed" is a real status (STATUS_OPTIONS already includes it) but also an
// automatic view server-side (routes/briefs.js's GET /) — a non-Approved,
// non-Delayed brief past its deadline shows here and gets promoted to it too.
// "Mentions" isn't a status at all — it's the mentionsMe=true query below.
// "Work Tracker" isn't a status either, and unlike Mentions it isn't even a
// filtered view of the same card grid — it renders a completely different
// screen (WorkTracker), so it's handled as its own branch throughout, not
// folded into the useBriefs status param.
const TABS = ['All', ...STATUS_OPTIONS, 'Mentions', 'Work Tracker'];

const Brief = () => {
    const [tab, setTab] = useState('All');
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editingBrief, setEditingBrief] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    // Super-user-only shortlist filter — narrows the list to one person's
    // briefs (as creator, assignee, or reviewer). null = no filter applied.
    const [personFilter, setPersonFilter] = useState(null);

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // The detail dialog's open/id state lives in the URL, not local state —
    // single source of truth, so a refresh or a shared /brief/:id link reopens
    // the same brief instead of dumping back to the plain list.
    const { briefId: detailBriefId } = useParams();

    // Opening Briefs at all counts as "seen it" — resets the sidebar badge that
    // NotificationsListener.jsx bumps on a 'brief-assigned-notify' event, same
    // precedent as WhatsAppInboxPage.jsx resetting ["whatsapp-unread"] on mount.
    useEffect(() => {
        queryClient.setQueryData(['brief-unread'], 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isMentionsTab = tab === 'Mentions';
    const isWorkTrackerTab = tab === 'Work Tracker';

    const handleTabChange = (_, value) => {
        setTab(value);
        setPage(1); // a different status filter has its own page count
    };

    // Debounced so search runs server-side (must match against the full result
    // set before paging, not just whatever page happens to already be loaded)
    // without firing a request on every keystroke.
    const debouncedSearch = useDebounce(search, 500);
    useEffect(() => {
        setPage(1); // a new search term has its own page count
    }, [debouncedSearch]);

    const { enqueueSnackbar } = useSnackbar();

    // Delete is creator-only (enforced server-side too) — read once from the login
    // cookie (see Signin.jsx's saveLoginData) to decide whether to show the action.
    const currentUserId = useMemo(() => {
        try {
            return JSON.parse(GetCookieByName('user'))?._id || null;
        } catch {
            return null;
        }
    }, []);

    const { data, isLoading, isError, error, refetch } = useBriefs(
        isMentionsTab || tab === 'All' ? 'all' : tab,
        page,
        debouncedSearch.trim(),
        isMentionsTab,
        !isWorkTrackerTab,
        personFilter?.id
    );
    const briefs = useMemo(() => data?.briefs ?? [], [data]);
    const totalPages = data?.pages ?? 1;
    const isSuperUser = data?.isSuperUser ?? false;

    // Same org roster the Assignee/Reviewer pickers already use elsewhere in
    // this feature — reused here for the person-filter's options so this adds
    // no new network call once that's warm.
    const { data: userOptions = [] } = useUserLookup();

    // Independent of the active tab/page/search — a first, minimal step
    // toward real notifications (a badge, not a push) per the plan to move
    // to a real notification bell later.
    const { data: mentionsCount = 0 } = useMentionsCount();

    const deleteMutation = useDeleteBrief();

    const handleCreate = () => {
        setEditingBrief(null);
        setModalOpen(true);
    };

    const handleView = (brief) => {
        navigate(`/brief/${brief.id}`);
    };

    const handleCloseDetail = () => {
        navigate('/brief');
    };

    const handleEdit = (brief) => {
        setEditingBrief(brief);
        setModalOpen(true);
    };

    const handleDelete = (brief) => {
        setDeleteTarget(brief);
    };

    const confirmDelete = () => {
        const brief = deleteTarget;
        setDeleteTarget(null);
        deleteMutation.mutate(
            { briefId: brief.id },
            {
                onSuccess: () => {
                    enqueueSnackbar('Brief deleted successfully', { variant: 'success' });
                    refetch();
                },
                onError: (err) => {
                    enqueueSnackbar(
                        err?.response?.data?.message || 'Failed to delete brief',
                        { variant: 'error' }
                    );
                },
            }
        );
    };

    if (!isWorkTrackerTab && isLoading && !briefs.length) {
        // BriefDetailDialog (and CreateBriefsModal, reachable from its Edit
        // button) still need to render here — otherwise a fresh deep link to
        // /brief/:briefId sits blocked behind this spinner for the main
        // list's own (unrelated) first load, even though the dialog fetches
        // its own brief/comments independently.
        return (
            <>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                    <CircularProgress />
                </Box>
                <CreateBriefsModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    initialBrief={editingBrief}
                    onSaved={() => refetch()}
                    isSuperUser={isSuperUser}
                />
                <BriefDetailDialog
                    open={Boolean(detailBriefId)}
                    briefId={detailBriefId}
                    onClose={handleCloseDetail}
                    onEdit={handleEdit}
                    currentUserId={currentUserId}
                    isSuperUser={isSuperUser}
                />
            </>
        );
    }

    
    return (
        <>
            <DashboardHeaderSection hideSearch />
            <Box
                sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    marginLeft: '65px',
                    marginTop: 8.2,
                    background: `linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)`,
                    minHeight: 'calc(90vh - 64px)'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Brief
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreate}
                        sx={{
                            background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                            boxShadow: '0 4px 14px rgba(255,82,0,0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                                opacity: 0.9,
                                boxShadow: '0 6px 20px rgba(255,82,0,0.45)',
                            },
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '10px',
                            px: 2.5,
                        }}
                    >
                        Create Brief
                    </Button>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 0,
                            display: 'inline-flex',
                            bgcolor: '#F3F4F6',
                            borderRadius: '12px',
                            p: 0.5,
                            '& .MuiTabs-indicator': { display: 'none' },
                            '& .MuiTabs-flexContainer': { gap: 0.5 },
                        }}
                    >
                        {TABS.map((value) => (
                            <Tab
                                key={value}
                                value={value}
                                label={value === 'Mentions' && mentionsCount > 0 ? `Mentions (${mentionsCount})` : labelForStatus(value)}
                                disableRipple
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: 13,
                                    borderRadius: '9px',
                                    minHeight: 36,
                                    px: 2.5,
                                    transition: 'all 0.2s ease',
                                    color: tab === value ? '#fff' : '#6B7280',
                                    background: tab === value
                                        ? 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)'
                                        : 'transparent',
                                    boxShadow: tab === value ? '0 2px 8px rgba(255,82,0,0.3)' : 'none',
                                    '&.Mui-selected': { color: '#fff' },
                                }}
                            />
                        ))}
                    </Tabs>

                    {!isWorkTrackerTab && (
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            {isSuperUser && (
                                <Autocomplete
                                    size="small"
                                    options={userOptions}
                                    getOptionLabel={(u) => u?.name ?? ''}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                    value={personFilter}
                                    onChange={(_, value) => {
                                        setPersonFilter(value);
                                        setPage(1); // a different person filter has its own page count
                                    }}
                                    sx={{
                                        width: { xs: '100%', sm: 200, md: 220 },
                                        '& .MuiOutlinedInput-root': {
                                            background: '#fff',
                                            borderRadius: '10px',
                                            fontSize: 13.5,
                                            '& fieldset': { borderColor: '#E5E7EB' },
                                            '&:hover fieldset': { borderColor: '#D1D5DB' },
                                            '&.Mui-focused fieldset': { borderColor: '#FF5200', borderWidth: '1.5px' },
                                        },
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Filter by person"
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PersonSearchOutlinedIcon sx={{ fontSize: 17, color: '#9CA3AF' }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            )}
                            <TextField
                                size="small"
                                placeholder="Search briefs"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 17, color: '#9CA3AF' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    width: { xs: '100%', sm: 200, md: 240 },
                                    '& .MuiOutlinedInput-root': {
                                        background: '#fff',
                                        borderRadius: '10px',
                                        fontSize: 13.5,
                                        '& fieldset': { borderColor: '#E5E7EB' },
                                        '&:hover fieldset': { borderColor: '#D1D5DB' },
                                        '&.Mui-focused fieldset': { borderColor: '#FF5200', borderWidth: '1.5px' },
                                    },
                                }}
                            />
                        </Box>
                    )}
                </Box>

                {isWorkTrackerTab ? (
                    <WorkTracker />
                ) : (
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '16px',
                        border: briefs.length ? 'none' : '1px solid #E5E7EB',
                        boxShadow: briefs.length ? 'none' : '0 4px 16px rgba(0,0,0,0.04)',
                        background: briefs.length ? 'transparent' : '#fff',
                        overflow: 'hidden',
                        p: briefs.length ? 0 : undefined,
                    }}
                >
                    {isError ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Couldn't load briefs</Typography>
                            <Typography color="text.secondary" sx={{ mb: 2 }}>
                                {error?.response?.data?.message || 'Something went wrong on our end.'}
                            </Typography>
                            <Button variant="outlined" onClick={() => refetch()} sx={{ textTransform: 'none', borderRadius: '10px' }}>
                                Try again
                            </Button>
                        </Box>
                    ) : isLoading && briefs.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress sx={{ color: '#FF5200' }} />
                        </Box>
                    ) : briefs.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                                {search.trim() ? 'No briefs match your search' : 'No briefs yet'}
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 2 }}>
                                {search.trim() ? 'Try a different search term.' : 'Create your first brief to get started.'}
                            </Typography>
                            {!search.trim() && (
                                <Button variant="contained" onClick={handleCreate} sx={{ textTransform: 'none', borderRadius: '10px' }}>
                                    Create Brief
                                </Button>
                            )}
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2.5}>
                                {briefs.map((brief) => (
                                    <Grid item xs={12} sm={6} md={4} key={brief.id}>
                                        <BriefCard brief={brief} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} currentUserId={currentUserId} isSuperUser={isSuperUser} />
                                    </Grid>
                                ))}
                            </Grid>
                            {totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                    <Pagination
                                        page={page}
                                        count={totalPages}
                                        onChange={(_, value) => setPage(value)}
                                        shape="rounded"
                                        sx={{
                                            '& .Mui-selected': {
                                                background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%) !important',
                                                color: '#fff',
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Paper>
                )}

                <CreateBriefsModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    initialBrief={editingBrief}
                    onSaved={() => refetch()}
                    isSuperUser={isSuperUser}
                />

                <BriefDetailDialog
                    open={Boolean(detailBriefId)}
                    briefId={detailBriefId}
                    onClose={handleCloseDetail}
                    onEdit={handleEdit}
                    currentUserId={currentUserId}
                    isSuperUser={isSuperUser}
                />

                <Dialog
                    open={Boolean(deleteTarget)}
                    onClose={() => setDeleteTarget(null)}
                    PaperProps={{ sx: { borderRadius: '16px' } }}
                >
                    <DialogTitle sx={{ fontWeight: 700 }}>Delete brief?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete "{deleteTarget?.title}"? This can't be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none', borderRadius: '10px' }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDelete}
                            color="error"
                            variant="contained"
                            sx={{ textTransform: 'none', borderRadius: '10px', boxShadow: 'none' }}
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
};

export default Brief;
